import {
  ADMIN_ENDPOINT,
  BASE_ENDPOINT,
  EXCLUSION_ENDPOINT,
  STUDENT_ENDPOINT
} from '@shared/properties';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterContentChecked,
  ElementRef,
  AfterViewInit,
  QueryList,
  ViewChildren,
  inject
} from '@angular/core';
import {
  AcademicYearTerm,
  exclusionsModel,
  nonSchoolDaysModel
} from '@exclusionmodule/models';
import { propertyTypeEnum, sortOrderEnum } from '@shared/models';
import {
  DataSortService,
  DateFormatService,
  AccessibilityService
} from '@shared/services';
import {
  ApiServiceService,
  NotificationService,
  MessagingService
} from '@oneeducation/app-core';
import { ConfirmationService } from 'primeng/api';
import {
  ACTION_TYPE,
  exclusionCategoryEnum
} from '@exclusionmodule/constants/exclusionCategory';
import { holidayTypesEnum } from '@schoolmanagement/constants';
import { Table } from 'primeng/table';
import { Select } from 'primeng/select';
import { SplitButton } from 'primeng/splitbutton';

@Component({
  selector: 'app-student-exclusion-list',
  templateUrl: './student-exclusion-list.component.html',
  styleUrls: ['./student-exclusion-list.component.scss'],
  standalone: false
})
export class StudentExclusionListComponent
  implements OnInit, AfterViewInit, AfterContentChecked
{
  private readonly apiService = inject(ApiServiceService);
  private readonly notificationService = inject(NotificationService);
  private readonly dataSortService = inject(DataSortService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dateFormatService = inject(DateFormatService);
  private readonly messagingService = inject(MessagingService);
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly elementRef = inject(ElementRef);

  @Input() schoolId: number;
  @Input() studId: number;
  @Input() studentName: string;
  @Input() isStudentOffRoll: boolean;
  @Input() schoolName: string;
  @Output() showExclusionDetails = new EventEmitter();
  @Output() refreshAcademicYearSummary: EventEmitter<boolean> =
    new EventEmitter();
  @ViewChild('allExclusionsList') allExclusionsList: Table;
  @ViewChild('sortOptionsDropDown') sortOptionsDropDown: Select;
  @ViewChild('showDropDown') showDropDown: Select;
  @Input() createExclusionActionType: ACTION_TYPE = ACTION_TYPE.NO_ACTION;
  @ViewChildren('addExclusionButton', { read: ElementRef })
  addExclusionButton: QueryList<ElementRef>;
  @ViewChildren('editExclusionButton', { read: ElementRef })
  editExclusionSplitButton: QueryList<ElementRef>;
  @ViewChild('editExclusionButton') editExclusionButton: SplitButton;

  exclusions: exclusionsModel[];
  filteredExclusions: exclusionsModel[];
  showExclusionsAcademicYearOptions: any[];
  selectedExclusionAcademicYearOption: string;
  academicYearTermList: AcademicYearTerm[];
  exclusionsListSortByOptions: any[] = [
    { label: 'Most recent', value: 'MR' },
    { label: 'Exclusion category', value: 'EC' }
  ];
  selectedExclusionsSortOption: string;
  noExclusionsFoundMessage = false;
  splitActionButtons = [];
  isExclusionPermOrAper = false;
  exclusionsEditingEnabled = true;
  nonSchoolDays: nonSchoolDaysModel[];
  lapsedSchoolDays: number;
  readonly ENTER_KEY: number = 13;
  tableRowIndex: number;
  isDeleteAction = false;

  ngOnInit() {
    this.setDefaultAcademicYearOptions();
    this.getStudentExclusions();
  }

  ngAfterViewInit(): void {
    this.setLableListBoxPaginator();
    //Though its always be single Add Exclusion btn, for subscribing to changes we have taken list.
    this.addExclusionButton.changes.subscribe((addButton) =>
      this.setFocusOnAddExclusionButton(addButton)
    );
    //Except in case of Create Excl cancel, we want focus on first row edit button
    if (this.createExclusionActionType !== ACTION_TYPE.CANCELLED) {
      this.editExclusionSplitButton.changes.subscribe((splitButtons) =>
        this.setFocusOnEditExclusionButton(splitButtons)
      );
    }
  }

  // Get current academic year and term for the base
  getCurrentAcademicYear() {
    const apiRoute = BASE_ENDPOINT.GetTermAndAcademicYear.replace(
      '{schoolId}',
      this.schoolId?.toString()
    );
    this.apiService.get<AcademicYearTerm>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.academicYearTermList = x.academicyearterm;
        this.onLoadSetUp();
      },
      error: () => {
        this.notificationService.showError(
          'Error getting academic year and term'
        );
        this.onLoadSetUp();
      }
    });
  }

  // Initial setup like filing the drop down and exclusion tables
  onLoadSetUp() {
    this.setAcademicYearOptions();
    this.setDefaults();
    this.filterExclusionByAcademicYear();
    this.sortExclusions();
  }

  // Fetch the student exclusion records using api
  // Base on the result create options for the academic year dropdown
  // Based on the dropdown value filter the exclusion records which will be displayed on the UI
  getStudentExclusions(): void {
    this.isExclusionPermOrAper = true;

    const apiRoute = EXCLUSION_ENDPOINT.GetExclusionsByStudent.replace(
      '{schoolId}',
      this.schoolId?.toString()
    ).replace('{studentId}', this.studId?.toString());
    this.apiService.getAll<exclusionsModel[]>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.exclusions = x.exclusion;
        //priority to display exclusions in list is set based on exclusion category
        this.exclusions.forEach(
          (z) =>
            (z.priority =
              z.categoryExtCode == exclusionCategoryEnum.EXT_CODE_PERMANENT
                ? 1
                : z.categoryExtCode == exclusionCategoryEnum.EXT_CODE_FIXED
                  ? 2
                  : z.categoryExtCode ==
                      exclusionCategoryEnum.EXT_CODE_SUSPENSION
                    ? 3
                    : z.categoryExtCode ==
                        exclusionCategoryEnum.EXT_CODE_LUNCHTIME
                      ? 4
                      : z.categoryExtCode ==
                          exclusionCategoryEnum.EXT_CODE_WARNING
                        ? 5
                        : 6)
        );
        //Request to refresh Student Summary page is set.  Alert then caught in Student Summary page.
        this.messagingService.sendAlert('refreshSummary');
        this.bindDeleteExclusionButton(this.exclusions);
        this.enableDisableAddExclusionButton();
        this.getCurrentAcademicYear();
        this.isAfterDateOffRollEligibleForEditing(this.exclusions);
      },
      error: (x: any) => {
        if (x.error.statusCode === 404) {
          this.filteredExclusions = [];
          this.exclusions = [];
          this.noExclusionsFoundMessage = true;
          //Add button to be visible to add exclusions after editing/deleting off-roll from student
          this.isExclusionPermOrAper = false;
          //Request to refresh Student Summary page is set.  Alert then caught in Student Summary page.
          this.messagingService.sendAlert('refreshSummary');

          // As there are no records, set focus on add button
          this.addExclusionButton.changes.subscribe((addButton) =>
            this.setFocusOnAddExclusionButton(addButton)
          );
          this.isDeleteAction = false;
          // set default values for show and sort by dropdowns
          this.selectedExclusionAcademicYearOption =
            this.showExclusionsAcademicYearOptions[0].value;
          this.selectedExclusionsSortOption =
            this.exclusionsListSortByOptions[0].value;
        } else {
          this.notificationService.showError('Error getting exclusions');
        }
      }
    });
    this.setDefaultAcademicYearOptions();
  }

  private getSchoolDaysDate(offRollDate: any) {
    const lastEditEligibleDate = new Date(offRollDate);
    let count = 1;

    while (count <= this.lapsedSchoolDays) {
      lastEditEligibleDate.setDate(lastEditEligibleDate.getDate() + 1);
      const nonSchoolDay: nonSchoolDaysModel = this.nonSchoolDays?.find(
        (x) =>
          lastEditEligibleDate.getTime() ==
            new Date(x.nonSchoolDate).getTime() &&
          (x.nonSchoolDayReason.trim() == holidayTypesEnum.HOLIDAY ||
            x.nonSchoolDayReason.trim() == holidayTypesEnum.STAFF_DAY ||
            x.nonSchoolDayReason.trim() == holidayTypesEnum.FORCED_CLOSURE)
      );

      //if non school day does not exist then increase the counter
      if (nonSchoolDay == null) {
        count++;
      }
    }

    return lastEditEligibleDate;
  }

  private bindIsEligibleForEditActions(exclusion: exclusionsModel[]) {
    let isPermanentExclusion = false;
    exclusion?.forEach((x) => {
      //If category is PERM or APER then only set to
      if (
        (x.categoryExtCode ==
          exclusionCategoryEnum.EXT_CODE_APPEAL_UPHELD_FROM_PERM ||
          x.categoryExtCode == exclusionCategoryEnum.EXT_CODE_PERMANENT) &&
        x.dateOffRoll
      ) {
        isPermanentExclusion = true;
        const dateOffRoll = this.getSchoolDaysDate(x.dateOffRoll);

        if (new Date().getTime() > dateOffRoll.getTime()) {
          this.exclusionsEditingEnabled = false;
        }
      }
    });

    if (!isPermanentExclusion) {
      if (
        exclusion?.filter(
          (x) =>
            x.categoryExtCode !=
              exclusionCategoryEnum.EXT_CODE_APPEAL_UPHELD_FROM_PERM &&
            x.categoryExtCode != exclusionCategoryEnum.EXT_CODE_PERMANENT
        ) &&
        this.isStudentOffRoll
      ) {
        this.exclusionsEditingEnabled = false;
      }
    }
  }

  private getNonSchoolDays(exclusions: exclusionsModel[]) {
    const studentId = exclusions[0].studentId;
    const apiRoute = this.getNonSchoolDaysApiRoute(studentId);
    this.apiService.getAll<nonSchoolDaysModel[]>(apiRoute, '').subscribe({
      next: (x: any) => {
        //Get non school days for student
        this.nonSchoolDays = x.nonschooldays;

        //Set is editing eligible for student exclusions
        this.bindIsEligibleForEditActions(exclusions);
      },
      error: () => {
        this.notificationService.showError('Error getting non school days');
      }
    });
  }

  private getNonSchoolDaysApiRoute(studentId: number) {
    return STUDENT_ENDPOINT.GetNonSchoolDays.replace(
      '{studentId}',
      studentId.toString()
    ).replace('{schoolId}', this.schoolId?.toString());
  }

  private isAfterDateOffRollEligibleForEditing(exclusion: exclusionsModel[]) {
    this.exclusionsEditingEnabled = true;
    const apiRoute = ADMIN_ENDPOINT.GetOneCloudConfigurations;
    this.apiService.get<any>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.lapsedSchoolDays = parseInt(x.record.lapsedSchoolDays);
        this.getNonSchoolDays(exclusion);
      },
      error: () => {
        this.notificationService.showError(
          'Something went wrong, please try again.'
        );
      }
    });
  }

  //common method for Filtered Exclusion
  bindDeleteExclusionButton(list: any[]) {
    this.splitActionButtons = [];
    list?.forEach((x) => {
      x.meetingFlag = x.meetings == true ? 'Yes' : 'No';
      const splitActionDeleteButton = [
        {
          label: 'Delete',
          styleClass: 'p-splitbutton p-component align-center',
          command: () => {
            this.deleteExclusionDetails(
              x.exclusionId,
              x.categoryExtCode,
              x.documentCount
            );
          }
        }
      ];
      this.splitActionButtons.push(splitActionDeleteButton);
    });
  }

  private enableDisableAddExclusionButton() {
    if (
      (this.exclusions != null || this.exclusions != undefined) &&
      this.exclusions.find(
        (x) =>
          (x.categoryExtCode === exclusionCategoryEnum.EXT_CODE_PERMANENT ||
            x.categoryExtCode ===
              exclusionCategoryEnum.EXT_CODE_APPEAL_UPHELD_FROM_PERM) &&
          new Date(x.startDate) <= new Date()
      ) != null
    ) {
      this.isExclusionPermOrAper = true;
    } else {
      this.isExclusionPermOrAper = false;
    }
  }

  // Set default values for show and sort by dropdowns
  setDefaults() {
    if (
      (this.selectedExclusionAcademicYearOption &&
        this.selectedExclusionAcademicYearOption.length === 0) ||
      this.selectedExclusionsSortOption === undefined
    ) {
      this.selectedExclusionAcademicYearOption =
        this.exclusions[0].academicYear;
      this.selectedExclusionsSortOption = 'MR';
    }
  }

  // Set the default option for academic year dropdowns
  setDefaultAcademicYearOptions() {
    this.showExclusionsAcademicYearOptions = [];
    this.showExclusionsAcademicYearOptions.push({
      label: 'All Exclusions',
      value: 'All'
    });
  }

  // Set the options for academic year dropdowns
  setAcademicYearOptions() {
    this.setDefaultAcademicYearOptions();
    this.exclusions
      .map((x) => x.academicYear)
      .filter((ay, ind, arr) => arr.indexOf(ay) == ind)
      .map((x) =>
        this.showExclusionsAcademicYearOptions.push({ label: x, value: x })
      );
    this.markCurrentAcademicYear();
  }

  // Mark the option from show dropdown as current academic year
  markCurrentAcademicYear() {
    const currentDateTime: Date = new Date();
    let currentAcademicYear = currentDateTime.getFullYear();

    if (this.academicYearTermList != null) {
      const academicYearStartDate = new Date(
        Math.min.apply(
          null,
          this.academicYearTermList
            ?.filter((x) => x.academicYear == currentDateTime.getFullYear())
            .map(function (x) {
              return new Date(x.startDate);
            })
        )
      );

      // Returns the month (0-11) in the specified date
      const currentMonth = currentDateTime.getMonth();

      // Decrement year with one if month is less then academic start date
      if (currentMonth < academicYearStartDate.getMonth()) {
        currentAcademicYear = currentAcademicYear - 1;
      }

      this.showExclusionsAcademicYearOptions.forEach((ay) => {
        if (
          currentAcademicYear != null &&
          currentAcademicYear == ay.value.split('/')[0]
        ) {
          ay.label = 'This academic year ' + ay.label;
        }
      });

      // Here we are always selecting second option in dropdown as this dropdown has sorted academic year list
      if (this.showExclusionsAcademicYearOptions.length > 1) {
        this.selectedExclusionAcademicYearOption =
          this.showExclusionsAcademicYearOptions[1].value;
      }
    }
  }

  // Filter the exclusions records by academic year
  filterExclusionByAcademicYear() {
    const filterOption = this.showExclusionsAcademicYearOptions.filter(
      (x) => x.value == this.selectedExclusionAcademicYearOption
    );

    if (filterOption && filterOption.length === 0) {
      this.selectedExclusionAcademicYearOption = 'All';
    }
    if (this.selectedExclusionAcademicYearOption == 'All') {
      this.filteredExclusions = this.exclusions;
    } else {
      this.filteredExclusions = this.exclusions.filter(
        (x) => x.academicYear == this.selectedExclusionAcademicYearOption
      );
    }

    this.sortExclusions();
  }

  // Sort the exclusions list
  sortExclusions() {
    if (this.selectedExclusionsSortOption == 'MR') {
      this.dataSortService.sortData(
        this.filteredExclusions,
        'startDate',
        propertyTypeEnum.date,
        sortOrderEnum.descending,
        'priority'
      );
    }
    if (this.selectedExclusionsSortOption == 'EC') {
      this.dataSortService.sortData(
        this.filteredExclusions,
        'category',
        propertyTypeEnum.string,
        sortOrderEnum.ascending
      );
    }
    this.bindDeleteExclusionButton(this.filteredExclusions);
  }

  showExclusionDetailsSection(
    exclusionId,
    accordionActiveState,
    isOffRollEditingEnabled
  ) {
    this.showExclusionDetails.emit({
      exclusionId: exclusionId,
      accordionActiveState: accordionActiveState,
      isOffRollEditingEnabled: isOffRollEditingEnabled
    });
    return false;
  }

  //delete Functionality
  deleteExclusionDetails(
    exclusionId: number,
    exclusionCatExtCode: string,
    documentCount?: number
  ) {
    if (
      documentCount !== undefined &&
      documentCount !== null &&
      documentCount > 0
    ) {
      this.notificationService.showWarning(
        'Cannot delete exclusion. Linked files exist for this exclusion'
      );
      this.setFocusOnSplitButton();
      return;
    }

    this.confirmationService.confirm({
      message:
        'Are you sure you want to delete the exclusion?' +
        '<br/>' +
        "This can't be undone.",
      header: 'Delete Exclusion',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      reject: () => {
        this.setFocusOnSplitButton();
      },
      accept: () => {
        this.isDeleteAction = true;
        const apiRoute = EXCLUSION_ENDPOINT.DeleteExclusion.replace(
          '{exclusionId}',
          exclusionId?.toString()
        );
        this.apiService.delete<any>(apiRoute, '').subscribe({
          next: (x: any) => {
            if (parseInt(x.statusCode) === 200) {
              this.notificationService.showSuccess(
                'Exclusion deleted successfully'
              );

              //rebind the exclusion
              this.getStudentExclusions();
              this.refreshAcademicYearSummary.emit(true);
              this.messagingService.sendAlert(exclusionCatExtCode);
              //Request to refresh Student Summary page is set.  Alert then caught in Student Summary page.
              this.messagingService.sendAlert('refreshSummary');
            }
          },
          error: (e: any) => {
            if (e.error !== null && e.error !== '') {
              if (e.error.statusCode === 404) {
                this.refreshAcademicYearSummary.emit(true);
              } else {
                this.notificationService.showError(e.error.message);
              }
            } else {
              this.notificationService.showError(
                'Something went wrong, please try again.'
              );
            }
          }
        });
      }
    });
  }

  private setAttributesForAlertBox() {
    if (this.elementRef) {
      this.accessibilityService.setAttributesForAlertBox(this.elementRef);
    }
  }

  ngAfterContentChecked(): void {
    this.setButtonDiscernibleTextPaginator();
    this.setMenuDiscernibleTextSplitButton();
    this.setAttributesForAlertBox();
    this.setDropdownAttributes();
    this.setAccessibleNameForShowDropdown();
    this.setAccessibleNameForSortByDropdown();
    this.setAccessibleNameForGridPaginationMenu();
    this.setLandmarkRoleForDropdowns();
    this.removeRoleAttributeForDropDowns();
    this.removeAriaExpandedAttributeForDropDowns();
    this.removeAriaExpandedAttributeForSplitButton();
  }

  // Remove aria expanded attributes to fix axe tool issues for split button
  private removeAriaExpandedAttributeForSplitButton() {
    this.accessibilityService.removeAriaExpandedAttributeForSplitButtons(
      this.allExclusionsList
    );
  }

  // Set attributes for grid pagination dropdown
  private setAccessibleNameForGridPaginationMenu() {
    if (this.allExclusionsList) {
      this.accessibilityService.setAccessibleNameForGridPaginationMenu(
        this.allExclusionsList,
        'Select number of exclusion records to view'
      );
    }
  }

  // Set accessible name for show dropdown
  private setAccessibleNameForShowDropdown() {
    if (this.showDropDown) {
      this.accessibilityService.setAccessibleNameForDropdownMenuButton(
        this.showDropDown,
        'Filter list of exclusions by'
      );
    }
  }

  // Set accessible name for sort by dropdown
  private setAccessibleNameForSortByDropdown() {
    if (this.sortOptionsDropDown) {
      this.accessibilityService.setAccessibleNameForDropdownMenuButton(
        this.sortOptionsDropDown,
        'Sort list of exclusions by'
      );
    }
  }

  // Set attributes of the dropdown
  private setDropdownAttributes() {
    // Set the properties of the sort dropdown
    if (this.sortOptionsDropDown) {
      const props = new Map<string, string>();
      props.set('title', 'Sort options');
      this.accessibilityService.setDropDownItemsAttributes(
        this.sortOptionsDropDown,
        props
      );
    }
    // Set the properties of the show dropdown
    if (this.showDropDown) {
      const props = new Map<string, string>();
      props.set('title', 'Show exclusions');
      this.accessibilityService.setDropDownItemsAttributes(
        this.showDropDown,
        props
      );
    }
  }

  private setMenuDiscernibleTextSplitButton() {
    const title = 'Manage Exclusions';
    this.accessibilityService.splitButtonSetTitle(
      this.allExclusionsList,
      title
    );
  }

  private setButtonDiscernibleTextPaginator() {
    this.accessibilityService.setTablePaginationControlsTitle(
      this.allExclusionsList,
      'Select number of exclusion records to view'
    );
  }

  private setLableListBoxPaginator() {
    const attributeMap = new Map();
    attributeMap.set('aria-label', 'Pages List');
    this.accessibilityService.setListBoxTableAttributes(
      this.allExclusionsList,
      attributeMap
    );
  }

  onRowSelect(event, accordionActiveState, exclusionsEditingEnabled) {
    if (event.originalEvent.keyCode === this.ENTER_KEY) {
      setTimeout(() => {
        this.showExclusionDetailsSection(
          event.data.exclusionId,
          accordionActiveState,
          exclusionsEditingEnabled
        );
      }, 0);
    }

    return false;
  }

  //find and assign the rowIndex to variable
  findTableRowIndex(rowIndex) {
    this.tableRowIndex = rowIndex;
  }

  //set focus on splitbutton from where delete popup is called
  setFocusOnSplitButton() {
    let isCurrentRowExists: boolean =
      this.tableRowIndex + 1 <= this.exclusions?.length ? true : false;

    if (
      !isCurrentRowExists &&
      this.isDeleteAction &&
      this.exclusions?.length > 0
    ) {
      isCurrentRowExists = true;
      this.tableRowIndex = this.tableRowIndex - 1;
    }

    setTimeout(() => {
      if (isCurrentRowExists) {
        this.editExclusionSplitButton
          .find(
            (x) =>
              x.nativeElement.attributes['ri'].nodeValue ===
              String(this.tableRowIndex)
          )
          .nativeElement.querySelector('.p-button-icon-only')
          ?.focus();

        this.isDeleteAction = false;
      } else {
        this.addExclusionButton.changes.subscribe((addButton) =>
          this.setFocusOnAddExclusionButton(addButton)
        );
      }
    }, 0);
  }

  //autoclose splitbutton when navigated outside the splitbutton through tab press
  autoCloseSplitButton(event: any) {
    if (event.target.closest('.p-element')) {
      event.target.closest('.p-tieredmenu')?.remove();
    }
  }

  private setFocusOnEditExclusionButton(splitButtons) {
    if (this.isDeleteAction) {
      this.setFocusOnSplitButton();
    } else {
      setTimeout(() => {
        splitButtons.first?.nativeElement.querySelector('button')?.focus();
      }, 0);
    }
  }

  //set focus on add exclusion button on page load
  private setFocusOnAddExclusionButton(addButton) {
    setTimeout(() => {
      addButton.first?.nativeElement.focus();
    }, 0);
  }

  // Ensures all page content is contained by landmarks
  private setLandmarkRoleForDropdowns() {
    if (this.showDropDown) {
      this.accessibilityService.setLandmarkRoleForDropDown(this.showDropDown);
    }
    if (this.sortOptionsDropDown) {
      this.accessibilityService.setLandmarkRoleForDropDown(
        this.sortOptionsDropDown
      );
    }
  }

  // Ensures role attribute has an appropriate value for the element
  private removeRoleAttributeForDropDowns() {
    if (this.showDropDown) {
      this.accessibilityService.removeRoleAttributeForDropDown(
        this.showDropDown
      );
    }
    if (this.sortOptionsDropDown) {
      this.accessibilityService.removeRoleAttributeForDropDown(
        this.sortOptionsDropDown
      );
    }
    if (this.allExclusionsList) {
      this.accessibilityService.removeRoleAttributeForDropDown(
        this.allExclusionsList
      );
    }
  }

  // Remove aria -expanded attribute
  private removeAriaExpandedAttributeForDropDowns() {
    if (this.showDropDown) {
      this.accessibilityService.removeAriaExpandedAttribute(this.showDropDown);
    }
    if (this.sortOptionsDropDown) {
      this.accessibilityService.removeAriaExpandedAttribute(
        this.sortOptionsDropDown
      );
    }
    if (this.allExclusionsList) {
      this.accessibilityService.removeAriaExpandedAttribute(
        this.allExclusionsList
      );
    }
  }
}
